import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ComposedChart, Bar
} from 'recharts';
import { TrendingUp, RefreshCw, MousePointerClick } from 'lucide-react';
import { GRANULARITIES, DATE_PRESETS, METRIC_COLORS } from './constants';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-4">
        <p className="font-semibold text-slate-800 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={`tt-${index}`} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-medium text-slate-800">
              {entry.name.includes('%') || entry.name.includes('Avg')
                ? `${entry.value.toFixed(1)}%`
                : entry.value.toLocaleString()
              }
            </span>
          </div>
        ))}
        <p className="text-xs text-[#2E5C8A] mt-2 border-t border-slate-100 pt-2 flex items-center gap-1">
          <MousePointerClick className="h-3 w-3" /> Click bar to drill down
        </p>
      </div>
    );
  }
  return null;
};

const METRIC_TOGGLES = [
  { id: 'total_hours', label: 'Total Hours' },
  { id: 'premium_hours', label: 'Premium Hours' },
  { id: 'non_premium_hours', label: 'Non-Premium Hours' },
  { id: 'premium_percent', label: 'Premium %' }
];

export const TrendChart = ({
  filteredData, granularity, datePreset,
  selectedMetrics, toggleMetric,
  showRollingAvg, setShowRollingAvg, rollingWindow,
  drilldownLoading, onDataPointClick
}) => {
  return (
    <>
      {/* Metric Toggles */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-600 mr-2">Show:</span>
        {METRIC_TOGGLES.map((metric) => (
          <button
            key={metric.id}
            onClick={() => toggleMetric(metric.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
              selectedMetrics.includes(metric.id)
                ? 'text-white shadow-sm'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
            style={{
              backgroundColor: selectedMetrics.includes(metric.id) ? METRIC_COLORS[metric.id] : undefined
            }}
            data-testid={`metric-toggle-${metric.id}`}
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{
                backgroundColor: selectedMetrics.includes(metric.id) ? 'white' : METRIC_COLORS[metric.id]
              }}
            />
            {metric.label}
          </button>
        ))}

        <div className="w-px h-6 bg-slate-300 mx-2" />

        <button
          onClick={() => setShowRollingAvg(!showRollingAvg)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
            showRollingAvg
              ? 'bg-purple-500 text-white shadow-sm'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
          data-testid="rolling-avg-toggle"
        >
          <TrendingUp className="w-3 h-3" />
          {rollingWindow}-Period Avg
        </button>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              {GRANULARITIES.find(g => g.id === granularity)?.label} Trend
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({DATE_PRESETS.find(p => p.id === datePreset)?.label})
              </span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
              <MousePointerClick className="h-3 w-3" />
              Click any bar to see what's driving the numbers
            </p>
          </div>
          <div className="flex items-center gap-3">
            {drilldownLoading && (
              <div className="flex items-center gap-2 text-sm text-[#2E5C8A]">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading drill-down...
              </div>
            )}
            {showRollingAvg && (
              <div className="flex items-center gap-2 text-xs text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                <div className="w-4 h-0.5 bg-purple-500 rounded" />
                <span>{rollingWindow}-period rolling average</span>
              </div>
            )}
          </div>
        </div>

        {filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart
              data={filteredData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="period_label"
                tick={{ fontSize: 11 }}
                stroke="#64748b"
                interval={filteredData.length > 20 ? Math.floor(filteredData.length / 10) : 0}
                angle={filteredData.length > 15 ? -45 : 0}
                textAnchor={filteredData.length > 15 ? "end" : "middle"}
                height={filteredData.length > 15 ? 60 : 30}
              />
              <YAxis
                yAxisId="hours"
                tick={{ fontSize: 12 }}
                stroke="#64748b"
                tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}
              />
              {(selectedMetrics.includes('premium_percent') || showRollingAvg) && (
                <YAxis
                  yAxisId="percent"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  stroke="#64748b"
                  tickFormatter={(value) => `${value}%`}
                  domain={[0, 'dataMax + 5']}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              <Legend />

              {selectedMetrics.includes('total_hours') && (
                <Bar
                  yAxisId="hours"
                  dataKey="total_hours"
                  name="Total Hours"
                  fill={METRIC_COLORS.total_hours}
                  radius={[4, 4, 0, 0]}
                  opacity={0.8}
                  cursor="pointer"
                  onClick={onDataPointClick}
                />
              )}
              {selectedMetrics.includes('premium_hours') && (
                <Bar
                  yAxisId="hours"
                  dataKey="premium_hours"
                  name="Premium Hours"
                  fill={METRIC_COLORS.premium_hours}
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={onDataPointClick}
                />
              )}
              {selectedMetrics.includes('non_premium_hours') && (
                <Bar
                  yAxisId="hours"
                  dataKey="non_premium_hours"
                  name="Non-Premium Hours"
                  fill={METRIC_COLORS.non_premium_hours}
                  radius={[4, 4, 0, 0]}
                  opacity={0.8}
                  cursor="pointer"
                  onClick={onDataPointClick}
                />
              )}
              {selectedMetrics.includes('premium_percent') && (
                <Line
                  yAxisId="percent"
                  type="monotone"
                  dataKey="premium_percent"
                  name="Premium %"
                  stroke={METRIC_COLORS.premium_percent}
                  strokeWidth={2}
                  dot={{ fill: METRIC_COLORS.premium_percent, r: 3 }}
                  activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                  opacity={showRollingAvg ? 0.4 : 1}
                />
              )}
              {showRollingAvg && (
                <Line
                  yAxisId="percent"
                  type="monotone"
                  dataKey="rolling_avg"
                  name={`${rollingWindow}-Period Avg`}
                  stroke={METRIC_COLORS.rolling_avg}
                  strokeWidth={3}
                  dot={false}
                  strokeDasharray="0"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[400px] flex items-center justify-center text-slate-500">
            <p>No data available for the selected period</p>
          </div>
        )}
      </div>
    </>
  );
};
